import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import { apiFetch } from "@/lib/api-client";
import { useAuth } from "@/hooks/useAuth";

/**
 * The reports this user may open.
 *
 * ⚠ The list comes from the server and is never assembled here. Which reports a person can
 * see is a grant decision (company-level, with per-user exceptions), and the client has no
 * business inferring it from a role — a contractor granted a Monday report and a contractor
 * who is not must differ because the server says so.
 *
 * Until the catalogue endpoint lands in chunk 2 this returns an empty list rather than a
 * plausible-looking guess: an empty sidebar is honest, an invented one is not.
 */

export interface ReportSummary {
  key: string;
  name: string;
  shortName: string;
  category: string;
  description: string;
  isReady: boolean;
  sortOrder: number;
}

interface ReportsContextType {
  reports: ReportSummary[];
  loading: boolean;
}

const ReportsContext = createContext<ReportsContextType>({ reports: [], loading: false });

export function ReportsProvider({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (state.status !== "authenticated") {
      setReports([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiFetch<{ reports: ReportSummary[] }>("/reports")
      .then((data) => {
        if (!cancelled) setReports(data.reports);
      })
      .catch(() => {
        // The endpoint does not exist yet (chunk 2). Stay empty and silent rather than
        // raising an error nobody can act on.
        if (!cancelled) setReports([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [state.status]);

  return <ReportsContext.Provider value={{ reports, loading }}>{children}</ReportsContext.Provider>;
}

export function useReports(): ReportsContextType {
  return useContext(ReportsContext);
}
